import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useCreatePromoCodeMutation,
  useUpdatePromoCodeMutation,
  useGetPromoCodeQuery,
  useSearchUsersForPromoCodeQuery,
} from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import { showSuccess, showError } from '../../../utils/toast';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

type DiscountType = 'amount' | 'percentage';
type UserType = 'all' | 'new' | 'old' | 'specific';
type ExpiryType = 'date' | 'count';

interface SelectedUser {
  id: number;
  name: string;
  email: string;
}

interface FormData {
  title: string;
  title_ar: string;
  code: string;
  min_amount: string;
  max_amount: string;
  discount_type: DiscountType;
  discount_value: string;
  user_type: UserType;
  expiry_type: ExpiryType;
  expiry_date: Date | null;
  expiry_count: string;
  status: 'active' | 'inactive';
}

const defaultForm: FormData = {
  title: '',
  title_ar: '',
  code: '',
  min_amount: '',
  max_amount: '',
  discount_type: 'amount',
  discount_value: '',
  user_type: 'all',
  expiry_type: 'date',
  expiry_date: null,
  expiry_count: '',
  status: 'active',
};

const AdminPromoCodeForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [formData, setFormData] = useState<FormData>(defaultForm);
  const [selectedUsers, setSelectedUsers] = useState<SelectedUser[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const userSearchRef = useRef<HTMLDivElement>(null);

  const { data: promoData, isLoading: isPromoLoading } = useGetPromoCodeQuery(id, { skip: !isEditMode });
  const { data: userSearchData } = useSearchUsersForPromoCodeQuery(userSearch, {
    skip: userSearch.length < 2,
  });
  const [createPromoCode, { isLoading: isCreating }] = useCreatePromoCodeMutation();
  const [updatePromoCode, { isLoading: isUpdating }] = useUpdatePromoCodeMutation();

  useEffect(() => {
    if (promoData?.data) {
      const d = promoData.data;
      setFormData({
        title: d.title ?? '',
        title_ar: d.title_ar ?? '',
        code: d.code ?? '',
        min_amount: d.min_amount != null ? String(d.min_amount) : '',
        max_amount: d.max_amount != null ? String(d.max_amount) : '',
        discount_type: d.discount_type ?? 'amount',
        discount_value: d.discount_value != null ? String(d.discount_value) : '',
        user_type: d.user_type ?? 'all',
        expiry_type: d.expiry_type ?? 'date',
        expiry_date: d.expiry_date ? new Date(d.expiry_date) : null,
        expiry_count: d.expiry_count != null ? String(d.expiry_count) : '',
        status: d.status ?? 'active',
      });
      if (d.users?.length) {
        setSelectedUsers(d.users.map((u: any) => ({ id: u.id, name: u.name, email: u.email })));
      }
    }
  }, [promoData]);

  // Close user dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (userSearchRef.current && !userSearchRef.current.contains(e.target as Node)) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const set = (field: keyof FormData, value: any) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const addUser = (user: SelectedUser) => {
    if (!selectedUsers.find((u) => u.id === user.id)) {
      setSelectedUsers((prev) => [...prev, user]);
    }
    setUserSearch('');
    setShowUserDropdown(false);
  };

  const removeUser = (userId: number) => {
    setSelectedUsers((prev) => prev.filter((u) => u.id !== userId));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload: any = {
        title: formData.title,
        title_ar: formData.title_ar,
        code: formData.code,
        discount_type: formData.discount_type,
        discount_value: Number(formData.discount_value),
        min_amount: formData.min_amount !== '' ? Number(formData.min_amount) : null,
        max_amount: formData.max_amount !== '' ? Number(formData.max_amount) : null,
        user_type: formData.user_type,
        expiry_type: formData.expiry_type,
        status: formData.status,
        expiry_date:
          formData.expiry_type === 'date' && formData.expiry_date
            ? formData.expiry_date.toISOString().split('T')[0]
            : null,
        expiry_count:
          formData.expiry_type === 'count' && formData.expiry_count !== ''
            ? Number(formData.expiry_count)
            : null,
      };

      if (formData.user_type === 'specific') {
        payload.user_ids = selectedUsers.map((u) => u.id);
      }

      if (isEditMode && id) {
        await updatePromoCode({ id, data: payload }).unwrap();
        showSuccess('Promo code updated successfully!');
      } else {
        await createPromoCode(payload).unwrap();
        showSuccess('Promo code created successfully!');
      }
      navigate('/admin/promo-codes');
    } catch (err: any) {
      showError(err?.data?.message || 'Failed to save promo code.');
    }
  };

  if (isEditMode && isPromoLoading) return <PageLoader />;

  const searchResults: SelectedUser[] = (userSearchData?.data || []).filter(
    (u: any) => !selectedUsers.find((s) => s.id === u.id)
  );

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                {isEditMode ? 'Edit Promo Code' : 'Add Promo Code'}
              </h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <span className="text-muted text-hover-primary">Home</span>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">
                  <button
                    type="button"
                    onClick={() => navigate('/admin/promo-codes')}
                    className="btn btn-link px-0 text-muted text-hover-primary"
                  >
                    Promo Codes
                  </button>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">{isEditMode ? 'Edit' : 'Add'}</li>
              </ul>
            </div>
          </div>
        </div>

        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card">
              <div className="card-header">
                <div className="card-title">
                  <h2>{isEditMode ? 'Edit Promo Code Details' : 'New Promo Code Details'}</h2>
                </div>
              </div>
              <div className="card-body">
                <form onSubmit={handleSubmit} className="row g-5">

                  {/* Title */}
                  <div className="col-md-6">
                    <label className="required form-label">Title</label>
                    <input
                      type="text"
                      className="form-control form-control-solid"
                      placeholder="e.g. Summer Sale"
                      value={formData.title}
                      onChange={(e) => set('title', e.target.value)}
                      required
                    />
                  </div>

                  {/* Title Arabic */}
                  <div className="col-md-6">
                    <label className="form-label">Title (Arabic)</label>
                    <input
                      type="text"
                      className="form-control form-control-solid"
                      placeholder="e.g. تخفيض الصيف"
                      dir="rtl"
                      value={formData.title_ar}
                      onChange={(e) => set('title_ar', e.target.value)}
                    />
                  </div>

                  {/* Promo Code */}
                  <div className="col-md-6">
                    <label className="required form-label">Promo Code</label>
                    <input
                      type="text"
                      className="form-control form-control-solid text-uppercase"
                      placeholder="e.g. SUMMER25"
                      value={formData.code}
                      onChange={(e) => set('code', e.target.value.toUpperCase())}
                      required
                    />
                    <div className="text-muted fs-7 mt-1">Must be unique. Letters and numbers only.</div>
                  </div>

                  {/* Status */}
                  <div className="col-md-6">
                    <label className="form-label">Status</label>
                    <select
                      className="form-select form-select-solid"
                      value={formData.status}
                      onChange={(e) => set('status', e.target.value)}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>

                  {/* Discount Type */}
                  <div className="col-md-6">
                    <label className="required form-label">Discount Type</label>
                    <select
                      className="form-select form-select-solid"
                      value={formData.discount_type}
                      onChange={(e) => set('discount_type', e.target.value as DiscountType)}
                    >
                      <option value="amount">Fixed Amount</option>
                      <option value="percentage">Percentage</option>
                    </select>
                  </div>

                  {/* Discount Value */}
                  <div className="col-md-6">
                    <label className="required form-label">
                      Discount Value {formData.discount_type === 'percentage' ? '(%)' : '(Amount)'}
                    </label>
                    <input
                      type="number"
                      className="form-control form-control-solid"
                      placeholder={formData.discount_type === 'percentage' ? '0 - 100' : '0.00'}
                      value={formData.discount_value}
                      onChange={(e) => set('discount_value', e.target.value)}
                      min="0"
                      max={formData.discount_type === 'percentage' ? 100 : undefined}
                      step={formData.discount_type === 'percentage' ? '1' : '0.01'}
                      required
                    />
                  </div>

                  {/* Min Amount */}
                  <div className="col-md-6">
                    <label className="form-label">Minimum Order Amount</label>
                    <input
                      type="number"
                      className="form-control form-control-solid"
                      placeholder="Leave empty for no minimum"
                      value={formData.min_amount}
                      onChange={(e) => set('min_amount', e.target.value)}
                      min="0"
                      step="0.01"
                    />
                  </div>

                  {/* Max Amount */}
                  <div className="col-md-6">
                    <label className="form-label">Maximum Discount Amount</label>
                    <input
                      type="number"
                      className="form-control form-control-solid"
                      placeholder="Leave empty for no cap"
                      value={formData.max_amount}
                      onChange={(e) => set('max_amount', e.target.value)}
                      min="0"
                      step="0.01"
                    />
                  </div>

                  {/* User Type */}
                  <div className="col-md-6">
                    <label className="required form-label">Applicable To</label>
                    <select
                      className="form-select form-select-solid"
                      value={formData.user_type}
                      onChange={(e) => {
                        set('user_type', e.target.value as UserType);
                        if (e.target.value !== 'specific') setSelectedUsers([]);
                      }}
                    >
                      <option value="all">All Users</option>
                      <option value="new">New Users</option>
                      <option value="old">Old Users</option>
                      <option value="specific">Specific Users</option>
                    </select>
                  </div>

                  {/* Expiry Type */}
                  <div className="col-md-6">
                    <label className="required form-label">Expiry Type</label>
                    <select
                      className="form-select form-select-solid"
                      value={formData.expiry_type}
                      onChange={(e) => set('expiry_type', e.target.value as ExpiryType)}
                    >
                      <option value="date">Expiry Date</option>
                      <option value="count">Usage Count</option>
                    </select>
                  </div>

                  {/* Conditional: Expiry Date */}
                  {formData.expiry_type === 'date' && (
                    <div className="col-md-6">
                      <label className="required form-label">Expiry Date</label>
                      <DatePicker
                        selected={formData.expiry_date}
                        minDate={new Date()}
                        onChange={(date: Date | null) => set('expiry_date', date)}
                        className="form-control form-control-solid"
                        placeholderText="Select expiry date"
                        dateFormat="yyyy-MM-dd"
                        isClearable
                        required
                      />
                    </div>
                  )}

                  {/* Conditional: Usage Count */}
                  {formData.expiry_type === 'count' && (
                    <div className="col-md-6">
                      <label className="required form-label">Maximum Usage Count</label>
                      <input
                        type="number"
                        className="form-control form-control-solid"
                        placeholder="e.g. 100"
                        value={formData.expiry_count}
                        onChange={(e) => set('expiry_count', e.target.value)}
                        min="1"
                        required
                      />
                    </div>
                  )}

                  {/* Specific Users */}
                  {formData.user_type === 'specific' && (
                    <div className="col-12">
                      <label className="required form-label">Select Users</label>

                      {/* Selected users tags */}
                      {selectedUsers.length > 0 && (
                        <div className="d-flex flex-wrap gap-2 mb-3">
                          {selectedUsers.map((u) => (
                            <div
                              key={u.id}
                              className="badge badge-light-primary d-flex align-items-center gap-2 py-2 px-3 fs-7"
                            >
                              <span>{u.name}</span>
                              <span className="text-muted fs-8">({u.email})</span>
                              <button
                                type="button"
                                className="btn btn-icon btn-xs ms-1"
                                onClick={() => removeUser(u.id)}
                                style={{ width: 16, height: 16, lineHeight: 1 }}
                              >
                                <i className="fas fa-times fs-9"></i>
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Search input with dropdown */}
                      <div className="position-relative" ref={userSearchRef}>
                        <div className="input-group">
                          <span className="input-group-text bg-light border-0">
                            <i className="fas fa-search text-muted"></i>
                          </span>
                          <input
                            type="text"
                            className="form-control form-control-solid"
                            placeholder="Search users by name, email or phone..."
                            value={userSearch}
                            onChange={(e) => {
                              setUserSearch(e.target.value);
                              setShowUserDropdown(true);
                            }}
                            onFocus={() => userSearch.length >= 2 && setShowUserDropdown(true)}
                          />
                        </div>
                        <div className="text-muted fs-7 mt-1">Type at least 2 characters to search</div>

                        {showUserDropdown && userSearch.length >= 2 && (
                          <div
                            className="dropdown-menu show w-100 p-0 shadow"
                            style={{ maxHeight: 260, overflowY: 'auto', zIndex: 1050 }}
                          >
                            {searchResults.length === 0 ? (
                              <div className="px-4 py-3 text-muted fs-7">No users found</div>
                            ) : (
                              searchResults.map((u: any) => (
                                <button
                                  key={u.id}
                                  type="button"
                                  className="dropdown-item d-flex align-items-center gap-3 py-3 px-4"
                                  onClick={() => addUser({ id: u.id, name: u.name, email: u.email })}
                                >
                                  <div
                                    className="symbol symbol-35px"
                                    style={{
                                      width: 35,
                                      height: 35,
                                      borderRadius: '50%',
                                      overflow: 'hidden',
                                      flexShrink: 0,
                                    }}
                                  >
                                    {u.picture ? (
                                      <img src={u.picture} alt={u.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    ) : (
                                      <div
                                        className="d-flex align-items-center justify-content-center w-100 h-100 fw-bold text-white fs-7"
                                        style={{ background: '#396A54' }}
                                      >
                                        {u.name?.charAt(0)?.toUpperCase()}
                                      </div>
                                    )}
                                  </div>
                                  <div>
                                    <div className="fw-semibold text-dark fs-7">{u.name}</div>
                                    <div className="text-muted fs-8">{u.email}</div>
                                  </div>
                                </button>
                              ))
                            )}
                          </div>
                        )}
                      </div>

                      {formData.user_type === 'specific' && selectedUsers.length === 0 && (
                        <div className="text-danger fs-7 mt-2">At least one user must be selected.</div>
                      )}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="col-12 d-flex justify-content-end gap-3 pt-3">
                    <button
                      type="button"
                      className="btn btn-light"
                      onClick={() => navigate('/admin/promo-codes')}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-base"
                      disabled={
                        isCreating ||
                        isUpdating ||
                        (formData.user_type === 'specific' && selectedUsers.length === 0)
                      }
                    >
                      <span className="indicator-label">{isEditMode ? 'Update' : 'Create'}</span>
                      {(isCreating || isUpdating) && (
                        <span className="indicator-progress">
                          Please wait...{' '}
                          <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                        </span>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPromoCodeForm;
